import api from './api';
import { ApiResponse, PageResponse } from '../types/api';
import {
  BrandRequestDTO,
  BrandResponseDTO,
  BrandFilters
} from '../types/brand';

const baseUrl = '/v1/marcas';

export const brandService = {
  // Helper: garantir que o campo de status siga o contrato 'ativo'
  mapBrand(raw: any): BrandResponseDTO {
    return {
      id: raw.id,
      nome: raw.nome,
      descricao: raw.descricao,
      // Usa 'ativo' se existir; caso contrário, mapeia de 'active'
      ativo: typeof raw.ativo === 'boolean' ? raw.ativo : !!raw.active,
      tenantId: raw.tenantId,
      createdAt: raw.createdAt,
      updatedAt: raw.updatedAt
    } as BrandResponseDTO;
  },

  // Listar marcas com paginação
  async list(params?: BrandFilters & { page?: number; size?: number; sort?: string }): Promise<ApiResponse<PageResponse<BrandResponseDTO>>> {
    const searchParams = new URLSearchParams();
    
    if (params?.page !== undefined) searchParams.append('page', params.page.toString());
    if (params?.size !== undefined) searchParams.append('size', params.size.toString());
    if (params?.sort) searchParams.append('sort', params.sort);
    
    // Filtros
    if (params?.nome) searchParams.append('nome', params.nome);
    if (params?.active !== undefined) searchParams.append('active', params.active.toString());
    
    const queryString = searchParams.toString();
    const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;
    
    const response = await api.get(url);

    // Verificar se a resposta já está no formato esperado ou se precisa ser envolvida
    if (response.data && typeof response.data === 'object') {
      // Se a resposta tem a estrutura ApiResponse
      if ('success' in response.data && 'data' in response.data) {
        const apiResp = response.data as ApiResponse<PageResponse<any>>;
        const page = apiResp.data;
        const mapped = {
          ...page,
          content: Array.isArray(page?.content)
            ? page.content.map(this.mapBrand)
            : []
        } as PageResponse<BrandResponseDTO>;
        return {
          ...apiResp,
          data: mapped
        } as ApiResponse<PageResponse<BrandResponseDTO>>;
      }
      // Se a resposta é diretamente os dados paginados
      else if ('content' in response.data && 'totalElements' in response.data) {
        const page = response.data as PageResponse<any>;
        const mapped = {
          ...page,
          content: Array.isArray(page?.content)
            ? page.content.map(this.mapBrand)
            : []
        } as PageResponse<BrandResponseDTO>;
        return {
          success: true,
          message: 'Dados carregados com sucesso',
          data: mapped,
          timestamp: new Date().toISOString()
        };
      }
      // Se a resposta é um array simples
      else if (Array.isArray(response.data)) {
        const arr = response.data.map(this.mapBrand) as BrandResponseDTO[];
        return {
          success: true,
          message: 'Dados carregados com sucesso',
          data: {
            content: arr,
            totalElements: arr.length,
            totalPages: 1,
            size: arr.length,
            number: 0,
            first: true,
            last: true
          },
          timestamp: new Date().toISOString()
        };
      }
    }

    // Fallback para resposta inesperada
    return {
      success: false,
      message: 'Formato de resposta inesperado',
      data: {
        content: [],
        totalElements: 0,
        totalPages: 0,
        size: 0,
        number: 0,
        first: true,
        last: true
      },
      timestamp: new Date().toISOString()
    };
  },

  // Listar todas as marcas (sem paginação) - útil para selects
  async listAll(params?: BrandFilters): Promise<ApiResponse<BrandResponseDTO[]>> {
    const searchParams = new URLSearchParams();
    
    // Filtros
    if (params?.nome) searchParams.append('nome', params.nome);
    if (params?.active !== undefined) searchParams.append('active', params.active.toString());
    
    const queryString = searchParams.toString();
    const url = queryString ? `${baseUrl}/all?${queryString}` : `${baseUrl}/all`;
    
    const response = await api.get(url);
    if (response.data && typeof response.data === 'object') {
      if ('success' in response.data && 'data' in response.data) {
        const apiResp = response.data as ApiResponse<any[]>;
        const mapped = Array.isArray(apiResp.data)
          ? apiResp.data.map(this.mapBrand)
          : [];
        return {
          ...apiResp,
          data: mapped
        } as ApiResponse<BrandResponseDTO[]>;
      } else if (Array.isArray(response.data)) {
        const mapped = response.data.map(this.mapBrand) as BrandResponseDTO[];
        return {
          success: true,
          message: 'Dados carregados com sucesso',
          data: mapped,
          timestamp: new Date().toISOString()
        };
      }
    }
    return {
      success: false,
      message: 'Formato de resposta inesperado',
      data: [],
      timestamp: new Date().toISOString()
    };
  },

  // Buscar marca por ID
  async getById(id: string): Promise<ApiResponse<BrandResponseDTO>> {
    const response = await api.get(`${baseUrl}/${id}`);

    // Verificar se a resposta já está no formato esperado ou se precisa ser envolvida
    if (response.data && typeof response.data === 'object') {
      // Se a resposta tem a estrutura ApiResponse
      if ('success' in response.data && 'data' in response.data) {
        const apiResp = response.data as ApiResponse<any>;
        const mapped = this.mapBrand(apiResp.data);
        return {
          ...apiResp,
          data: mapped
        } as ApiResponse<BrandResponseDTO>;
      }
      // Se a resposta é diretamente o objeto da marca
      else if ('id' in response.data && 'nome' in response.data) {
        return {
          success: true,
          message: 'Marca carregada com sucesso',
          data: this.mapBrand(response.data),
          timestamp: new Date().toISOString()
        };
      }
    }

    // Fallback para resposta inesperada
    return {
      success: false,
      message: 'Marca não encontrado ou formato de resposta inesperado',
      data: null as any,
      timestamp: new Date().toISOString()
    };
  },

  // Criar nova marca
  async create(data: BrandRequestDTO): Promise<ApiResponse<BrandResponseDTO>> {
    const response = await api.post<ApiResponse<BrandResponseDTO>>(baseUrl, data);
    return response.data;
  },

  // Atualizar marca
  async update(id: string, data: BrandRequestDTO): Promise<ApiResponse<BrandResponseDTO>> {
    const response = await api.put<ApiResponse<BrandResponseDTO>>(`${baseUrl}/${id}`, data);
    return response.data;
  },

  // Deletar marca
  async delete(id: string): Promise<ApiResponse<void>> {
    const response = await api.delete<ApiResponse<void>>(`${baseUrl}/${id}`);
    return response.data;
  },

  // Ativar marca
  async activate(id: string): Promise<ApiResponse<BrandResponseDTO>> {
    const response = await api.patch(`${baseUrl}/${id}/ativar`);
    // Se a resposta for 204 (No Content), consideramos como sucesso
    if (response.status === 204) {
      return { 
        success: true, 
        data: null as any, 
        message: 'Marca ativada com sucesso',
        timestamp: new Date().toISOString()
      };
    }
    return response.data;
  },

  // Desativar marca
  async deactivate(id: string): Promise<ApiResponse<BrandResponseDTO>> {
    const response = await api.patch(`${baseUrl}/${id}/desativar`);
    // Se a resposta for 204 (No Content), consideramos como sucesso
    if (response.status === 204) {
      return { 
        success: true, 
        data: null as any, 
        message: 'Marca desativada com sucesso',
        timestamp: new Date().toISOString()
      };
    }
    return response.data;
  },

  // Verificar se nome da marca já existe
  async checkNameExists(nome: string, excludeId?: string): Promise<ApiResponse<boolean>> {
    const searchParams = new URLSearchParams();
    searchParams.append('nome', nome);
    if (excludeId) searchParams.append('excludeId', excludeId);
    
    const response = await api.get<ApiResponse<boolean>>(`${baseUrl}/check-name?${searchParams.toString()}`);
    return response.data;
  }
};

export default brandService;